/**
 * Custom Field Conflict Detection Utilities
 * 
 * Detects and categorizes custom field conflicts during paste operations.
 * A conflict occurs when a pasted field has the same name as an existing
 * field but a different type.
 */

import { FIELD_DEFINITIONS } from '../../config/fieldVisibility/definitions';

/**
 * Gets all reserved field names that cannot be used for custom fields.
 * Dynamically reads from FIELD_DEFINITIONS so future additions are automatically included.
 * 
 * @returns {Set<string>} Lowercase reserved field names
 */
export function getReservedFieldNames() {
  const names = new Set();
  for (const field of FIELD_DEFINITIONS) {
    // Add the main label (lowercase for case-insensitive comparison)
    if (field.label) {
      names.add(field.label.toLowerCase());
    }
    // Also add the field ID as a reserved name (e.g., "dueDate", "estimatedDurationMinutes")
    if (field.id) {
      names.add(field.id.toLowerCase());
    }
  }
  // Add some additional common names that might conflict
  names.add('name');
  names.add('title');
  names.add('mission');
  names.add('task');
  return names;
}

/**
 * Analyzes clipboard custom field definitions against existing campaign fields
 * and categorizes them into: reusable (same name+type), conflicting (same name, different type),
 * reservedNameConflicts (uses a built-in field name), and new (no name match).
 * 
 * @param {Array} clipboardFields - Custom field definitions from clipboard
 * @param {Array} existingFields - Custom fields already in the campaign
 * @returns {{
 *   reusable: Map<number, string>,      // clipboardIndex -> existingFieldId (can reuse existing)
 *   conflicts: Array<{ clipboardIndex: number, clipboardField: object, existingField: object }>,
 *   reservedNameConflicts: Array<{ clipboardIndex: number, clipboardField: object, reservedName: string }>,
 *   newFields: Array<{ clipboardIndex: number, field: object }>,
 * }}
 */
export function detectCustomFieldConflicts(clipboardFields = [], existingFields = []) {
  // Build a map of existing field names (lowercase) to their full definition
  const existingByName = new Map();
  for (const field of existingFields) {
    const lowerName = (field.name || '').trim().toLowerCase();
    if (lowerName) {
      existingByName.set(lowerName, field);
    }
  }
  
  // Get reserved field names
  const reservedNames = getReservedFieldNames();
  
  const reusable = new Map(); // clipboardIndex -> existingFieldId
  const conflicts = []; // fields that need renaming (same name, different type)
  const reservedNameConflicts = []; // fields that use reserved names
  const newFields = []; // fields that can be added as-is
  
  clipboardFields.forEach((clipboardField, idx) => {
    const lowerName = (clipboardField.name || '').trim().toLowerCase();
    
    if (!lowerName) {
      // Empty name - skip
      return;
    }
    
    const existing = existingByName.get(lowerName);
    
    if (existing) {
      if (existing.type === clipboardField.type) {
        // Same name AND same type - reuse the existing field
        reusable.set(idx, existing.id);
      } else {
        // Same name but DIFFERENT type - conflict!
        conflicts.push({
          clipboardIndex: idx,
          clipboardField,
          existingField: existing,
        });
      }
    } else if (reservedNames.has(lowerName)) {
      // Name matches a built-in field - treat as conflict
      reservedNameConflicts.push({
        clipboardIndex: idx,
        clipboardField,
        reservedName: clipboardField.name,
      });
    } else {
      // No name match - this is a new field
      newFields.push({ clipboardIndex: idx, field: clipboardField });
    }
  });
  
  return { reusable, conflicts, reservedNameConflicts, newFields };
}

/**
 * Builds a field ID remapping for pasted tasks based on conflict resolution.
 * 
 * @param {Map<number, string>} reusable - clipboardIndex -> existingFieldId
 * @param {Map<number, string>} renamed - clipboardIndex -> newFieldId (from renamed fields)
 * @param {Map<number, string>} added - clipboardIndex -> newFieldId (from newly added fields)
 * @returns {Map<number, string>} Complete mapping of clipboardIndex -> finalFieldId
 */
export function buildFieldIdMapping(reusable, renamed, added) {
  const mapping = new Map();
  
  for (const [idx, id] of reusable.entries()) {
    mapping.set(idx, id);
  }
  for (const [idx, id] of renamed.entries()) {
    mapping.set(idx, id);
  }
  for (const [idx, id] of added.entries()) {
    mapping.set(idx, id);
  }
  
  return mapping;
}

/**
 * Remaps custom field values in a task from clipboard indices to field IDs.
 * 
 * @param {object} customFieldValues - Task's customFieldValues (clipboardIndex -> value)
 * @param {Map<number, string>} fieldIdMapping - clipboardIndex -> finalFieldId
 * @param {Array} clipboardFieldDefs - Original clipboard field definitions
 * @param {Map<number, Map<number, string>>} optionIdMappings - clipboardFieldIndex -> (optionIndex -> optionId)
 * @returns {object} Remapped customFieldValues (fieldId -> value)
 */
export function remapCustomFieldValues(
  customFieldValues,
  fieldIdMapping,
  clipboardFieldDefs,
  optionIdMappings = new Map()
) {
  if (!customFieldValues || Object.keys(customFieldValues).length === 0) {
    return undefined;
  }
  
  const remapped = {};
  
  for (const [fieldIndexStr, value] of Object.entries(customFieldValues)) {
    const fieldIndex = parseInt(fieldIndexStr, 10);
    const newFieldId = fieldIdMapping.get(fieldIndex);
    
    if (!newFieldId) {
      // Field was skipped (conflict not resolved)
      continue;
    }
    
    const fieldDef = clipboardFieldDefs[fieldIndex];
    
    // For select fields, remap option index to option ID
    if (fieldDef?.type === 'select' && typeof value === 'number') {
      const optionMapping = optionIdMappings.get(fieldIndex);
      if (optionMapping) {
        const newOptionId = optionMapping.get(value);
        if (newOptionId) {
          remapped[newFieldId] = newOptionId;
        }
      }
    } else {
      // For other types, keep the value as-is
      remapped[newFieldId] = value;
    }
  }
  
  return Object.keys(remapped).length > 0 ? remapped : undefined;
}

/**
 * Gets all existing custom field names as a Set (lowercase).
 * Useful for duplicate detection.
 * 
 * @param {Array} existingFields - Custom fields already in the campaign
 * @returns {Set<string>} Lowercase field names
 */
export function getExistingFieldNamesSet(existingFields = []) {
  return new Set(
    existingFields
      .map(f => (f.name || '').trim().toLowerCase())
      .filter(Boolean)
  );
}
